import React from 'react';
import { Box, Typography, Grid, Skeleton, TextField, MenuItem } from '@mui/material';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { youtubeAPI } from '../../services/api';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';

const AudienceInsights: React.FC = () => {
  const [days, setDays] = React.useState(30);
  // Fetch reporting summary for demographics and device data
  const { data: reportingSummary, isLoading } = useQuery({
    queryKey: ['youtube-reporting-summary', days],
    queryFn: () => youtubeAPI.getReportingSummary(days),
    staleTime: 60000
  });

  // Fetch comprehensive data for more accurate demographics
  const { data: comprehensiveData, isLoading: comprehensiveLoading } = useQuery({
    queryKey: ['youtube-comprehensive-audience', days],
    queryFn: () => youtubeAPI.getComprehensiveDashboard(days),
    staleTime: 60000
  });

  // Top countries from /insights/geography (real data in personal_video_geography)
  const { data: geo } = useQuery({
    queryKey: ['audience-geo', days],
    queryFn: () => youtubeAPI.getGeography(days),
    staleTime: 60000
  });

  // Playback locations (reveals WATCH vs SHORTS_FEED vs BROWSE breakdown)
  const { data: playback } = useQuery({
    queryKey: ['audience-playback', days],
    queryFn: () => youtubeAPI.getPlaybackLocations(days),
    staleTime: 60000
  });

  // Devices + OS from the dedicated insights endpoint (has real decoded values)
  const { data: devicesData } = useQuery({
    queryKey: ['audience-devices-os', days],
    queryFn: () => youtubeAPI.getDevicesOS(days),
    staleTime: 60000
  });

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Process demographics data with comprehensive fallback
  const demographicsData = React.useMemo(() => {
    // Try comprehensive data first
    if (comprehensiveData?.demographics && comprehensiveData.demographics.length > 0) {
      const validDemographics = comprehensiveData.demographics.filter(
        (demo: any) => demo.age_group && demo.gender && demo.views_percentage > 0
      );
      
      if (validDemographics.length > 0) {
        return validDemographics.map((demo: any, index: number) => ({
          name: `${demo.age_group} ${demo.gender}`,
          value: demo.views_percentage,
          color: colors[index % colors.length]
        }));
      }
    }
    
    // Fallback to reporting summary
    if (!reportingSummary?.demographics) return [];
    
    const validDemographics = reportingSummary.demographics.filter(
      (demo: any) => demo.age_group !== 'UNKNOWN' && parseFloat(demo.total_views_pct) > 0
    );
    
    if (validDemographics.length === 0) {
      // Return sample data if no real data available
      return [
        { name: 'Demographics data', value: 100, color: '#8884d8' }
      ];
    }
    
    return validDemographics.map((demo: any, index: number) => ({
      name: `${demo.age_group} ${demo.gender}`,
      value: parseFloat(demo.total_views_pct),
      color: colors[index % colors.length]
    }));
  }, [reportingSummary?.demographics, comprehensiveData?.demographics]);

  // Prefer the dedicated /insights/devices-os endpoint which returns real
  // decoded device_type and operating_system values (MOBILE, DESKTOP, TABLET,
  // TV / ANDROID, IOS, WINDOWS, MACINTOSH, CHROME_OS). Either field may be
  // null because the two dimensions live in separate personal_* tables and
  // are unioned by the API.
  const deviceData = React.useMemo(() => {
    const rows = devicesData?.devices || [];
    if (rows.length === 0) return [];
    const totalViews = rows.reduce((sum: number, d: any) => sum + Number(d.views || 0), 0);
    return rows.map((d: any, index: number) => {
      const parts = [];
      if (d.device_type) parts.push(d.device_type);
      if (d.operating_system) parts.push(d.operating_system);
      const name = parts.join(' / ') || 'UNKNOWN';
      const pct = totalViews > 0 ? (Number(d.views) / totalViews) * 100 : 0;
      return {
        name,
        value: Number(d.views) || 0,
        pct: Number(pct.toFixed(1)),
        color: colors[index % colors.length],
      };
    });
  }, [devicesData?.devices]);

  return (
    <Box>
      <SectionTitle>AUDIENCE INSIGHTS &amp; DEMOGRAPHICS</SectionTitle>
      <Panel label="FILTERS" sx={{ mb: 2, p: 2 }}>
        <TextField size="small" select label="Window" value={days} onChange={e => setDays(parseInt(e.target.value, 10))}>
          {[7, 14, 30, 60, 90].map(v => <MenuItem key={v} value={v}>{v} days</MenuItem>)}
        </TextField>
      </Panel>

      <Grid container spacing={3}>
        {/* Age Distribution */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="AGE & GENDER DISTRIBUTION" sx={{ height: 400 }}>
            {(isLoading || comprehensiveLoading) ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={demographicsData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}${demographicsData.length === 1 ? '' : '%'}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {demographicsData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
            {!isLoading && demographicsData.length === 0 && (
              <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mt: 2 }}>
                No demographic data in the last {days} days.
              </Typography>
            )}
          </Panel>
        </Grid>

        {/* Device Types */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="DEVICE & OS DISTRIBUTION" sx={{ height: 400 }}>
            {(isLoading || comprehensiveLoading) ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.name}: ${entry.pct}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {deviceData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => `${(v ?? 0).toLocaleString()} views`} />
                </PieChart>
              </ResponsiveContainer>
            )}
            {!isLoading && deviceData.length === 0 && (
              <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mt: 2 }}>
                No device/OS data in the last {days} days.
              </Typography>
            )}
          </Panel>
        </Grid>

        {/* Top Countries */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="TOP COUNTRIES" sx={{ height: 400 }}>
            {(geo?.countries || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={(geo?.countries || []).slice(0, 10)}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : String(v)} />
                  <YAxis type="category" dataKey="country" width={60} />
                  <Tooltip formatter={(v: number) => v.toLocaleString()} />
                  <Bar dataKey="views" fill="#00ff41" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="textSecondary">No geography data in window.</Typography>
            )}
          </Panel>
        </Grid>

        {/* Playback Locations */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="PLAYBACK LOCATIONS" sx={{ height: 400 }}>
            {(playback?.locations || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={(playback?.locations || []).map((p: any) => ({
                      name: p.location,
                      value: Number(p.views) || 0,
                    }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${(value ?? 0).toLocaleString()}`}
                  >
                    {(playback?.locations || []).map((_: any, idx: number) => (
                      <Cell key={`cell-${idx}`} fill={colors[idx % colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => v.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="textSecondary">No playback data in window.</Typography>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AudienceInsights;
